import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LaboursService } from '../../services/labours.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  LabourBasicInfo,
  LabourCheckIn,
  LabourCheckInRequest,
  LabourCheckInResponse,
  LabourCheckOutResponse,
} from '../../production-inventory.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import moment from 'moment';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { UserRole } from '../../../employees/employees.model';

@Component({
  selector: 'app-labours-daily-attendance',
  standalone: false,
  templateUrl: './labours-daily-attendance.component.html',
  styleUrl: './labours-daily-attendance.component.scss',
})
export class LaboursDailyAttendanceComponent implements OnInit, OnDestroy {
  @Input() warehouseControl: FormControl = new FormControl();
  @Input() wareHouses: RawMaterialWarehouse[] = [];

  protected checkedInLabours: LabourCheckIn[] = [];
  protected labours: LabourBasicInfo[] = [];
  protected selectedLabourIds: string[] = [];
  protected selectedCheckOutLabourIds: string[] = [];
  protected checkOutEndTime: string = '';
  protected checkOutErrors: { labour_id: string; detail: string }[] = [];
  protected modalRef!: NgbModalRef;
  protected startTime: string = '';
  protected isAttendanceDataLoading: boolean = false;
  protected checkOutForm!: FormGroup;
  protected totalPayable: number = 0;
  protected selectedLabour: LabourCheckIn | null = null;
  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected selectedWarehouseId: string | null = null;
  protected searchTerm: string = '';
  protected filterWarehouseId: string = '';
  protected currentUser: any = null;

  private subscriptions: Subscription = new Subscription();
  private laboursSubscription!: Subscription;
  private searchDebounceTimer!: any;
  protected currentPage: number = 1;

  @ViewChild('exportModal') exportModalRef!: TemplateRef<any>;

  protected exportFromDate: string = '';
  protected exportToDate: string = '';
  protected exportLabourId: string | null = null;
  protected isExporting: boolean = false;
  protected exportError: any = null;

  protected readonly UserRole = UserRole;

  constructor(
    private laboursService: LaboursService,
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {
    this.checkOutForm = this.fb.group({
      end_time: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    // Initialize with existing control value if any, though we prefer local selection now
    if (this.warehouseControl?.value) {
      this.selectedWarehouseId = this.warehouseControl.value;
    }

    this.subscriptions.add(
      this.warehouseControl.valueChanges.subscribe((warehouseId: string) => {
        this.selectedWarehouseId = warehouseId;
        this.currentPage = 1;
        this.getInitialData();
      })
    );

    this.getInitialData();
  }

  protected openCheckInModal(content: TemplateRef<any>): void {
    this.modalRef = this.modalService.open(content, { size: 'md', backdrop: 'static' });
  }

  protected saveCheckIn(): void {
    const payload: LabourCheckInRequest = {
      labour_ids: this.selectedLabourIds,
      start_time: new Date(this.startTime).toISOString(),
    };

    this.laboursSubscription = this.laboursService.check_in(payload).subscribe({
      next: (newCheckedInLabour: LabourCheckInResponse) => {
        this.checkedInLabours.push(...newCheckedInLabour.checked_in);
        this.selectedLabourIds = [];
        this.startTime = '';

        this.currentPage = 1;
        this.getInitialData();
        this.modalRef?.dismiss();
      },
    });
  }

  protected onLabourToggle(labourId: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedLabourIds.push(labourId);
    } else {
      this.selectedLabourIds = this.selectedLabourIds.filter(id => id !== labourId);
    }
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.getInitialData();
    }, 500);
  }

  protected onWarehouseFilterChange(): void {
    this.currentPage = 1;
    this.getInitialData();
  }

  private getInitialData(): void {
    this.isAttendanceDataLoading = true;

    const effectiveWarehouse = this.filterWarehouseId || this.selectedWarehouseId;
    const q: Record<string, any> = { page: this.currentPage };

    if (effectiveWarehouse) {
      q['warehouse'] = effectiveWarehouse;
    }

    if (this.searchTerm) {
      q['full_name'] = this.searchTerm;
    }

    const labours$ = this.laboursService.lighter_list({ warehouse: effectiveWarehouse });
    const todaysAttendance$ = this.laboursService.todays_attendance(q);

    this.laboursSubscription = forkJoin([labours$, todaysAttendance$])
      .pipe(finalize(() => (this.isAttendanceDataLoading = false)))
      .subscribe({
        next: ([laboursData, checkedInLaboursResponse]: [LabourBasicInfo[], PaginatedResponse<LabourCheckIn>]) => {
          this.labours = laboursData;
          this.checkedInLabours = checkedInLaboursResponse.results ?? [];
          this.totalCount = checkedInLaboursResponse.count;
          this.hasNextPage = !!checkedInLaboursResponse.next;
          this.hasPreviousPage = !!checkedInLaboursResponse.previous;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getInitialData();
  }

  protected openCheckOutModal(content: TemplateRef<any>, preSelectLabour: LabourCheckIn | null = null): void {
    this.selectedCheckOutLabourIds = preSelectLabour ? [preSelectLabour.labour.id] : [];
    this.checkOutErrors = [];
    this.checkOutForm.patchValue({ end_time: moment().format('YYYY-MM-DDTHH:mm') });
    this.modalRef = this.modalService.open(content, { size: 'md', backdrop: 'static' });
  }

  protected onCheckOutLabourToggle(labourId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedCheckOutLabourIds.push(labourId);
    } else {
      this.selectedCheckOutLabourIds = this.selectedCheckOutLabourIds.filter(id => id !== labourId);
    }
  }

  protected toggleSelectAllCheckOut(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedCheckOutLabourIds = this.checkableLabours.map(l => l.labour.id);
    } else {
      this.selectedCheckOutLabourIds = [];
    }
  }

  protected get checkableLabours(): LabourCheckIn[] {
    return this.checkedInLabours.filter(l => !l.end_time);
  }

  protected areAllCheckOutSelected(): boolean {
    const checkable = this.checkableLabours;
    return checkable.length > 0 && checkable.every(l => this.selectedCheckOutLabourIds.includes(l.labour.id));
  }

  protected submitCheckOut(): void {
    if (this.checkOutForm.invalid || this.selectedCheckOutLabourIds.length === 0) return;

    const payload = {
      labour_ids: this.selectedCheckOutLabourIds,
      end_time: new Date(this.checkOutForm.value.end_time).toISOString(),
    };

    this.laboursSubscription = this.laboursService.check_out(payload).subscribe({
      next: (response: LabourCheckOutResponse) => {
        response.checked_out.forEach(updated => {
          const idx = this.checkedInLabours.findIndex(l => l.id === updated.id);
          if (idx !== -1) this.checkedInLabours[idx] = updated;
        });

        this.checkOutErrors = response.errors ?? [];

        if (this.checkOutErrors.length === 0) {
          this.checkOutForm.reset();
          this.selectedCheckOutLabourIds = [];
          this.modalRef?.dismiss();
        }
      },
    });
  }

  protected isAlreadyCheckedIn(labour: LabourBasicInfo) {
    return this.checkedInLabours.some(ci => ci.labour.id === labour.id);
  }

  // Select All toggle
  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      // Select all labours who are not already checked in
      this.selectedLabourIds = this.labours.filter(l => !this.isAlreadyCheckedIn(l)).map(l => l.id);
    } else {
      this.selectedLabourIds = [];
    }
  }

  // Check if all selectable labours are selected
  areAllSelectableSelected(): boolean {
    const selectableLabours = this.labours.filter(l => !this.isAlreadyCheckedIn(l));
    return selectableLabours.length > 0 && selectableLabours.every(l => this.selectedLabourIds.includes(l.id));
  }

  protected openExportModal(): void {
    this.exportFromDate = '';
    this.exportToDate = '';
    this.exportLabourId = null;
    this.exportError = null;
    this.modalService.open(this.exportModalRef, { size: 'md' });
  }

  protected onExportSubmit(modalRef: any): void {
    if (!this.exportFromDate || !this.exportToDate) return;

    this.isExporting = true;
    this.exportError = null;

    const q: Record<string, any> = {
      from_date: this.exportFromDate,
      to_date: this.exportToDate,
    };
    if (this.exportLabourId) q['labour_id'] = this.exportLabourId;

    this.laboursService
      .export_attendance(q)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'attendance_sheet.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          modalRef.close();
        },
        error: err => {
          this.exportError = err;
        },
      });
  }

  ngOnDestroy(): void {
    this.laboursSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
